import graphql from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { useFlags } from "@/utils/launchDarkly";

import { useRequestRestrictionStatuses_booking$key } from "./__generated__/useRequestRestrictionStatuses_booking.graphql";

export type RequestRestrictionStatusRow = Readonly<{
  type: "BLOCK" | "EVENT" | "RESTRICTION";
  blockingScope: "FULL" | "PARTIAL";
  label: string;
  startAt: string;
  endAt: string;
  bookingIndex?: number | null;
}>;

export const useRequestRestrictionStatusesFragmentString = graphql`
  fragment useRequestRestrictionStatuses_booking on Booking {
    requestRestrictionStatuses {
      type
      blockingScope
      bookingIndex
      label
      startAt
      endAt
    }
    startSlot {
      __typename
    }
    ... on StandardBooking {
      request {
        id
      }
    }
    ... on LessonBooking {
      request {
        id
      }
    }
  }
`;

const restrictionTypeSortOrder = (
  type: RequestRestrictionStatusRow["type"]
): number => {
  switch (type) {
    case "BLOCK":
      return 0;
    case "EVENT":
      return 1;
    case "RESTRICTION":
      return 2;
  }
};

const sortStatuses = (
  statuses: readonly RequestRestrictionStatusRow[]
): RequestRestrictionStatusRow[] => {
  return [...statuses].sort((a, b) => {
    if (a.blockingScope !== b.blockingScope) {
      return a.blockingScope === "FULL" ? -1 : 1;
    }

    return restrictionTypeSortOrder(a.type) - restrictionTypeSortOrder(b.type);
  });
};

export const useRequestRestrictionStatuses = (
  bookingRef: useRequestRestrictionStatuses_booking$key | null
): RequestRestrictionStatusRow[] => {
  const { hideUnavailableTimes } = useFlags();
  const data = useFragment(
    useRequestRestrictionStatusesFragmentString,
    bookingRef
  );

  return useMemo(() => {
    if (!hideUnavailableTimes) {
      return [];
    }

    const statuses =
      data?.requestRestrictionStatuses?.filter(
        (status): status is NonNullable<typeof status> => status != null
      ) ?? [];

    return sortStatuses(statuses);
  }, [data?.requestRestrictionStatuses, hideUnavailableTimes]);
};

export const useHasBlockingRequestRestrictionStatus = (
  bookingRef: useRequestRestrictionStatuses_booking$key | null
): boolean => {
  const statuses = useRequestRestrictionStatuses(bookingRef);

  return statuses.some(
    (status) =>
      status.blockingScope === "FULL" &&
      (status.type === "BLOCK" || status.type === "EVENT")
  );
};

export const useShouldShowRequestRestrictionBanners = (
  bookingRef: useRequestRestrictionStatuses_booking$key | null
): boolean => {
  const { hideUnavailableTimes } = useFlags();
  const data = useFragment(
    useRequestRestrictionStatusesFragmentString,
    bookingRef
  );

  return (
    !!hideUnavailableTimes &&
    data?.startSlot?.__typename === "QueueSlot" &&
    data?.request?.id != null
  );
};
